<template>
  <Modal v-model="open" max-width="600" persistent :loading>
    <template #title>Add Email Address</template>
    <template #content>
      <div class="pa-4">
        <!-- <v-icon color="warning" size="40">mdi-alert-circle</v-icon> -->
        <div class="mt-3 mb-2 text-h6">Add Email Address</div>
        <div class="text-body-2 mb-4">
          You do not have an email set up. An email address is required to
          recover important notifications and updates. Would you like to add one
          now? You can do this later in settings.
        </div>
        <v-form v-model="valid">
          <v-text-field
            v-model="email"
            label="Email"
            variant="outlined"
            density="compact"
            :rules="emailRules"
            hide-details="auto"
          />
          <v-checkbox
            v-model="dontShowAgain"
            label="Don't show me this again"
            class="mt-2"
            hide-details
          />
        </v-form>
      </div>
    </template>
    <template #actions>
      <v-btn-secondary @click="close">close</v-btn-secondary>
      <v-btn-primary
        :disabled="!valid && !dontShowAgain"
        @click="saveEmail"
        :loading
      >
        Save
      </v-btn-primary>
    </template>
  </Modal>
  <div class="d-flex justify-center align-center">
    <v-snackbar
      v-model="snackbar.open"
      :timeout="3000"
      :color="snackbar.color"
      location="top"
      style="margin-right: 200px"
    >
      {{ snackbar.text }}
      <template v-slot:actions>
        <v-btn variant="text" @click="snackbar.open = false">Close</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import Modal from './Modal.vue';
import { useRootStore } from '../stores/root.js';
import { storeToRefs } from 'pinia';
import { accountsPost, appSyncApolloClient } from '../helpers/api.js';
import gql from 'graphql-tag';
import { createAdminLog } from '../helpers/createAdminLog.js';

const { user } = storeToRefs(useRootStore());
const open = defineModel();
const email = ref('');
const valid = ref(false);
const dontShowAgain = ref(false);
const loading = ref(false);

const emailRules = [(v) => /.+@.+\..+/.test(v) || 'Email must be valid'];
const snackbar = ref({
  open: false,
  text: '',
  color: '',
});

async function saveEmail() {
  loading.value = true;
  try {
    if (valid.value && email.value) {
      await editUser({
        userId: user.value.userId,
        oldDisplayData: user.value.displayData,
        displayData: {
          ...user.value.displayData,
          alertEmail: email.value,
        },
        orgId: user.value.orgId,
      });
      snackbar.value.text = 'Email saved successfully.';
    } else if (dontShowAgain.value) {
      await accountsPost('accounts/disableEmailPopup', {
        userId: user.value.userId,
      });
      snackbar.value.text = 'Email popup disabled.';
    }
    snackbar.value.open = true;
    snackbar.value.color = 'success';
  } catch (error) {
    console.error('Error', error);
    snackbar.value.open = true;
    snackbar.value.text = dontShowAgain.value
      ? 'Failed to disable email popup'
      : 'Failed to save email. Please try again.';
    snackbar.value.color = 'error';
    return;
  } finally {
    loading.value = false;
    open.value = false;
  }
}

async function updateUser({ userId, displayData, orgId }) {
  delete displayData.__typename;
  if (displayData.address) delete displayData.address.__typename;

  return appSyncApolloClient.mutate({
    variables: {
      userId: `user-${userId.replace('user-', '')}`,
      displayData,
      orgId,
    },
    mutation: gql`
      mutation editUser(
        $userId: String!
        $displayData: DisplayDataInput!
        $orgId: String
        $geid: String
      ) {
        editUser(
          userId: $userId
          displayData: $displayData
          orgId: $orgId
          geid: $geid
        ) {
          email
        }
      }
    `,
  });
}

async function editUser({ orgId, userId, displayData, oldDisplayData }) {
  let editUserResponse;
  // edit the user
  editUserResponse = await updateUser({
    userId,
    displayData,
    orgId,
  });
  await createAdminLog({
    logType: 'User',
    message: 'Updated user info',
    endpointName: 'editUser',
    previousValue: {
      userId,
      displayData: oldDisplayData,
    },
    updatedValue: { userId, displayData, orgId },
    name: displayData.name,
  });
  return editUserResponse;
}

function close() {
  open.value = false;
}
</script>

<style scoped>
.pa-4 {
  padding: 16px;
  text-align: center;
}
</style>
